'use client';

import React from 'react';
import { m } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { Share2, FileText, Phone, ShoppingCart, Search, TrendingUp, ShieldCheck, ArrowRight } from 'lucide-react';
import Link from 'next/link';

const roadmapSteps = [
    {
        num: '01',
        title: 'Foundation',
        agent: 'The Builder',
        description: 'Before traffic comes, we need a place to catch it. "The Builder" constructs high-converting landing pages and lead capture forms tailored to your offer. No coding required.',
        icon: FileText,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
    },
    {
        num: '02',
        title: 'Infiltration',
        agent: 'The Hacker',
        description: 'Traffic isn\'t waited for—it\'s taken. "The Hacker" scans Reddit, X (Twitter), and niche communities for people asking about your solution. It drafts helpful, non-spammy replies that drop your link, driving high-intent traffic instantly.',
        icon: Search,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
    },
    {
        num: '03',
        title: 'Authority',
        agent: 'The Editor',
        description: 'Trust converts. "The Editor" writes deep-dive SEO blogs and LinkedIn thought-leadership posts. It ensures when people search for your niche, they find you at the top, positioning your brand as the industry expert.',
        icon: Share2, // closest to 'PenTool' or content distribution
        color: 'text-purple-600',
        bg: 'bg-purple-50',
    },
    {
        num: '04',
        title: 'Scale',
        agent: 'The Buyer',
        description: 'Organic is great; paid is faster. "The Buyer" manages your Google and Meta ads. It kills losing ads instantly and scales winning creatives, protecting your budget while hunting for maximum ROAS.',
        icon: ShoppingCart,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
    },
    {
        num: '05',
        title: 'Capture',
        agent: 'The Receptionist',
        description: 'Never miss a lead. "The Receptionist" answers every phone call 24/7 with a human-like voice. It answers FAQs, qualifies leads, and books appointments directly onto your calendar while you sleep.',
        icon: Phone,
        color: 'text-pink-600',
        bg: 'bg-pink-50',
    },
    {
        num: '06',
        title: 'Optimization',
        agent: 'Mission Control',
        description: 'You stay in the captain\'s chair. All agents report back to Mission Control. You see a unified feed of leads found, ads scaled, content written, and calls answered. You approve the big strategic moves; they handle the execution.',
        icon: ShieldCheck,
        color: 'text-neutral-900',
        bg: 'bg-neutral-100',
    },
];

export default function RoadmapPage() {
    return (
        <div className="min-h-screen bg-[#FAFAFA] text-neutral-900 font-sans selection:bg-orange-100 selection:text-orange-900">
            <Navbar />

            <main className="pt-32 pb-24 px-6">
                {/* Hero Section */}
                <div className="max-w-4xl mx-auto text-center mb-24">
                    <m.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-5xl md:text-6xl font-bold mb-6 tracking-tight"
                    >
                        The Axist <span className="text-orange-600">Roadmap</span>
                    </m.h1>
                    <m.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl text-neutral-500 font-medium leading-relaxed"
                    >
                        How 5 AI Agents work in perfect sync to build your marketing machine.
                        <br className="hidden md:block" /> From zero traffic to market dominance.
                    </m.p>
                </div>

                {/* Timeline Section */}
                <div className="max-w-5xl mx-auto relative">
                    {/* Vertical Line */}
                    <div className="absolute left-[28px] md:left-1/2 top-0 bottom-0 w-0.5 bg-neutral-200 -z-10 md:-translate-x-1/2" />

                    <div className="space-y-16">
                        {roadmapSteps.map((step, index) => {
                            const Icon = step.icon;
                            const isEven = index % 2 === 0;

                            return (
                                <m.div
                                    key={step.title}
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    className={`flex flex-col md:flex-row gap-8 items-start ${isEven ? 'md:flex-row-reverse' : ''}`}
                                >
                                    {/* Content Side */}
                                    <div className={`flex-1 ${isEven ? 'md:text-left' : 'md:text-right'} pl-16 md:pl-0`}>
                                        <div className={`inline-flex items-center gap-2 mb-3 bg-white border border-neutral-200 rounded-full px-3 py-1 shadow-sm ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${step.bg} ${step.color} bg-opacity-50`}>
                                                {step.agent}
                                            </span>
                                            <span className="text-xs font-semibold text-neutral-400">STEP {step.num}</span>
                                        </div>
                                        <h3 className="text-3xl font-bold text-neutral-900 mb-4">{step.title}</h3>
                                        <p className="text-lg text-neutral-500 leading-relaxed font-medium">
                                            {step.description}
                                        </p>
                                    </div>

                                    {/* Center Icon */}
                                    <div className="absolute left-0 md:left-1/2 w-14 h-14 bg-white rounded-full border-4 border-[#FAFAFA] md:-translate-x-1/2 flex items-center justify-center shadow-md z-10">
                                        <div className={`w-10 h-10 rounded-full ${step.bg} flex items-center justify-center`}>
                                            <Icon className={step.color} size={20} />
                                        </div>
                                    </div>

                                    {/* Empty Space for alignment */}
                                    <div className="flex-1 hidden md:block" />
                                </m.div>
                            );
                        })}
                    </div>
                </div>

                {/* Final CTA */}
                <m.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    className="max-w-4xl mx-auto mt-32 bg-neutral-900 rounded-[2.5rem] p-12 text-center text-white relative overflow-hidden"
                >
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-neutral-800 to-transparent opacity-50" />

                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-5xl font-bold mb-6">Ready to launch your fleet?</h2>
                        <p className="text-lg text-neutral-400 mb-10 max-w-2xl mx-auto">
                            Deploy all 5 agents today. Start your 14-day free trial and see them in action.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link href="/login?mode=signup">
                                <button className="px-8 py-4 bg-orange-600 hover:bg-orange-500 text-white font-bold rounded-xl transition-all shadow-lg hover:shadow-orange-500/25 flex items-center justify-center gap-2 w-full sm:w-auto">
                                    Start Free Trial
                                    <ArrowRight size={20} />
                                </button>
                            </Link>
                            <Link href="/demo">
                                <button className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 backdrop-blur-sm w-full sm:w-auto">
                                    Book a Demo
                                </button>
                            </Link>
                        </div>
                    </div>
                </m.div>
            </main>

            <Footer />
        </div>
    );
}
